import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { AutoTooltip, Icon, Row, Text, TooltipContent } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type {
	AclPreviewResponse,
	AclProviderResult,
	AclRevokeErrorCode,
	EntityMetadata,
	SpawnShareDialogSmartParamsBase,
} from "./types";
import { previewAclChanges } from "./preview-acl-changes";
import { ForEach } from "@mdotm/utils/react-extra";
import { splitAt } from "@mdotm/utils/collections";
import {
	buildPerEntityAcls,
	type AclChangeAnalysis,
	type LinkedEntityConflictsInfo,
	type OwnershipTransferInfo,
} from "./build-per-entity-acls";

export type { AclChangeAnalysis } from "./build-per-entity-acls";
export { buildPerEntityAcls } from "./build-per-entity-acls";

// --- Preview response parsing ---

function extractLinkedEntityConflicts(
	response: AclPreviewResponse,
	entityMetadataList: EntityMetadata[],
): LinkedEntityConflictsInfo | null {
	const metadataByUuid = new Map(entityMetadataList.map((m) => [m.uuid, m]));
	const conflicts: Array<{
		entityUuid: string;
		entityName: string;
		failedUserIds: string[];
		errors: AclRevokeErrorCode[];
	}> = [];
	const safeEntityUuids = new Set<string>();
	for (const [entityUuid, results] of Object.entries(response)) {
		const failures = results.filter((r) => !r.successful);
		if (failures.length > 0) {
			const meta = metadataByUuid.get(entityUuid);
			conflicts.push({
				entityUuid,
				entityName: meta?.name ?? entityUuid,
				failedUserIds: failures.map((f) => f.userId),
				errors: failures.map((f) => f.error).filter((e): e is AclRevokeErrorCode => !!e),
			});
		} else {
			safeEntityUuids.add(entityUuid);
		}
	}

	return conflicts.length > 0 ? { conflicts, safeEntityUuids } : null;
}

// --- Detectors ---

function detectOwnershipTransfer(
	originalAclResults: Array<AclProviderResult>,
	newAcl: RichAccessControl[],
): OwnershipTransferInfo | null {
	const originalOwnerIds = new Set(
		originalAclResults.flatMap(
			(result) =>
				result.customerEntityAcl
					?.filter((entry) => entry.userId && entry.permissions?.includes("OWNER"))
					.map((entry) => entry.userId!) ?? [],
		),
	);

	const newOwnerEntry = newAcl.find((entry) => entry.permissions?.includes("OWNER"));
	if (!newOwnerEntry?.userId) return null;

	if (originalOwnerIds.has(newOwnerEntry.userId)) return null;

	const previousOwnerId = [...originalOwnerIds].at(0);
	if (!previousOwnerId) return null;

	return {
		previousOwnerId,
		newOwnerId: newOwnerEntry.userId,
		newOwnerName:
			[newOwnerEntry.name, newOwnerEntry.surname].filter(Boolean).join(" ") ||
			newOwnerEntry.email ||
			newOwnerEntry.userId,
	};
}

// --- Analysis ---

export async function analyzeAclChanges(
	originalAclResults: Array<AclProviderResult>,
	entityMetadataList: EntityMetadata[],
	newAcl: RichAccessControl[],
): Promise<AclChangeAnalysis> {
	const ownershipTransfer = detectOwnershipTransfer(originalAclResults, newAcl);

	let linkedEntityConflicts: LinkedEntityConflictsInfo | null = null;
	if (entityMetadataList.length > 0) {
		const previewResponse = await previewAclChanges(entityMetadataList, newAcl);
		linkedEntityConflicts = extractLinkedEntityConflicts(previewResponse, entityMetadataList);
	}

	return { newAcl, linkedEntityConflicts, ownershipTransfer, entityMetadataList, originalAclResults };
}

// --- Warning Dialogs ---

async function showOwnershipTransferWarning({
	saveAclHandler,
	analysis,
}: {
	analysis: Omit<AclChangeAnalysis, "ownershipTransfer"> & {
		ownershipTransfer: NonNullable<AclChangeAnalysis["ownershipTransfer"]>;
	};
	saveAclHandler: SpawnShareDialogSmartParamsBase["saveAclHandler"];
}): Promise<void> {
	const aclByEntity = buildPerEntityAcls(analysis);

	spawnYesNoDialog({
		size: "large",
		header: (
			<Row gap={8} alignItems="center">
				<Icon size={24} icon="Icon-full-alert" color={themeCSSVars.palette_W400} />
				<Text type="Title/S">Transfer ownership</Text>
			</Row>
		),
		yesButton: "Proceed",
		noButton: "Cancel",
		children: (
			<Text>
				You are transferring ownership to {analysis.ownershipTransfer.newOwnerName}. Your permissions will change to
				editor.
			</Text>
		),
		onSubmitAsync: async () => {
			await saveAclHandler({ aclByEntity });
		},
	}).catch(noop);
}

async function showLinkedEntityConflictsWarning(
	conflicts: LinkedEntityConflictsInfo,
	analysis: AclChangeAnalysis,
	saveAclHandler: SpawnShareDialogSmartParamsBase["saveAclHandler"],
): Promise<void> {
	const aclByEntity = buildPerEntityAcls(analysis);
	const conflictNames = conflicts.conflicts.map((c) => c.entityName);

	const [shown, remaining] = splitAt(conflictNames, 3);

	spawnYesNoDialog({
		size: "large",
		header: (
			<Row gap={8} alignItems="center">
				<Icon size={24} icon="Icon-full-alert" color={themeCSSVars.palette_W400} />
				<Text type="Title/S">Remove access</Text>
			</Row>
		),
		yesButton: "Proceed",
		noButton: "Cancel",
		children: (
			<Text>
				You cannot remove access to {shown.join(", ")}
				{remaining.length > 0 && (
					<>
						{" and "}
						<AutoTooltip
							trigger={({ innerRef }) => (
								<span ref={innerRef} style={{ textDecoration: "underline", cursor: "pointer" }}>
									{remaining.length} more
								</span>
							)}
							hideArrow
							position="top"
						>
							<TooltipContent>
								<ul className="pl-3">
									<ForEach collection={remaining}>{(name) => <li className="list-disc">{name}</li>}</ForEach>
								</ul>
							</TooltipContent>
						</AutoTooltip>
					</>
				)}{" "}
				because {shown.length === 1 ? "it is used" : "they are used"} by portfolios of selected users
			</Text>
		),
		onSubmitAsync: async () => {
			await saveAclHandler({ aclByEntity });
		},
	}).catch(noop);
}

// --- Warning Orchestrator ---

export async function processAclWarnings(
	analysis: AclChangeAnalysis,
	saveAclHandler: SpawnShareDialogSmartParamsBase["saveAclHandler"],
): Promise<{ aclByEntity: Record<string, RichAccessControl[]> } | null> {
	if (analysis.ownershipTransfer) {
		showOwnershipTransferWarning({
			saveAclHandler,
			analysis: { ...analysis, ownershipTransfer: analysis.ownershipTransfer },
		});
		return null;
	}

	if (analysis.linkedEntityConflicts) {
		showLinkedEntityConflictsWarning(analysis.linkedEntityConflicts, analysis, saveAclHandler);
		return null;
	}

	return { aclByEntity: buildPerEntityAcls(analysis) };
}
